import React, { useState } from 'react'
import { connect, useSelector } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { flatten } from 'lodash-es'
import {
  AuthorisedUser,
  CollateralAllocation,
  CollateralAllocationDetail,
  DealActions,
  DealDetail,
  DealSaveAllocationsArgs,
  HaircutType,
  SaveAllocationsSource,
  Severity,
  TradingType,
  ValidationIssue
} from 'api'
import { ModalFooter, ModalTitle, ModalWindow } from 'view/components/panels'
import { DialogField, DialogRow } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { Tabs } from 'view/components/tabs'
import { AllocationHeader } from './AllocationHeader'
import { ReadOnlyCollateralOverviewPanel } from 'view/shared/collateralBasket/ReadOnlyCollateralBasketOverviewPanel'
import { AvailableProductsPanel } from './AvailableProductsPanel'
import { AllocationsPanel } from './AllocationPanel'
import {
  AllocationContainer,
  Content,
  DetailsContainer,
  OpenFinDropzone,
  TabbedContent,
  UploadCollateralAllocationsButton
} from '../allocationDialogStyles'
import { ModalConsumerProps, NotificationType, UnhappyModal, withModal } from 'view/shared/modal'
import { createCollateralAllocationDetailsSelector } from 'selectors/collateralAllocationSelectors'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import { createDealDetailSelector } from 'selectors/dealSelectors'
import { NotificationActions, ShowDialogArgs } from 'store/notification/notificationActions'
import { AllocationData } from '../../collateralBasketAllocationUpload/collateralBasketAllocationUploadParse'
import { canAllocateCollaterals } from 'view/routes/deals/components/utils'
import { downloadUrl, isOpenFin } from 'utils'
import { Locale, Translation } from 'locale'
import { Dropzone } from 'view/shared/upload'
import { CollateralBasketAllocationUploadDialog } from 'view/shared/collateralBasketAllocationUpload'
import { SplitButtonType } from 'view/components/menus'
import allocationsTemplateUrl from './assets/AllocationsTemplate.xlsx'
import { startConsiderationOverrideEnabledSelector } from 'selectors/featureSelectors'

export type CollateralBasketAllocationDialogProps = OwnProps & StateProps & DispatchProps

interface OwnProps extends ModalConsumerProps {
  dealId: string
  onClose?: () => unknown
}

interface StateProps {
  dealDetail: DealDetail | undefined
  allocationDetails: CollateralAllocationDetail[] | null
  authorisedUser: AuthorisedUser
}

interface DispatchProps {
  onCancelAllocations: (dealId: string) => unknown
  onSaveAllocations: (args: DealSaveAllocationsArgs) => unknown
  onAllocate: (dealId: string) => unknown
  onShowDialog: (dialogArgs: ShowDialogArgs) => unknown
}

const dealIdSelector = (_: {}, props: CollateralBasketAllocationDialogProps) => props.dealId

const mapStateToProps = createStructuredSelector({
  dealDetail: createDealDetailSelector(dealIdSelector),
  allocationDetails: createCollateralAllocationDetailsSelector(dealIdSelector),
  authorisedUser: createAuthorisedUserSelector()
})

const mapDispatchToProps = {
  onCancelAllocations: DealActions.cancelAllocations,
  onSaveAllocations: DealActions.saveAllocations,
  onShowDialog: NotificationActions.showDialog,
  onAllocate: DealActions.allocate
}

export const CollateralBasketAllocationDialogComponent: React.FunctionComponent<CollateralBasketAllocationDialogProps> = props => {
  const {
    dealDetail,
    allocationDetails,
    onClose,
    authorisedUser,
    onSaveAllocations,
    onCancelAllocations,
    onShowDialog,
    onAllocate,
    showModal
  } = props

  const [clipboardIssues, setClipboardIssues] = useState<ValidationIssue[]>([])

  const tabs = [Translation.TC_OVERVIEW, Translation.TC_PRODUCTS]

  const [selectedTab, setSelectedTab] = useState<Translation>(Translation.TC_PRODUCTS)

  const [calculatingTradingType, onSetCalculatingTradingType] = useState(dealDetail?.deal?.tradingType ?? TradingType.NOTIONAL)

  const startConsiderationOverrideEnabled = useSelector(startConsiderationOverrideEnabledSelector)

  if (!dealDetail) return null
  const {
    deal: { dealId, direction, basketAllocation, notional, startConsideration, tradingType, repoType },
    collateralBasket,
    productSector,
    ownOrder
  } = dealDetail

  if (!collateralBasket || !basketAllocation || !allocationDetails) {
    return <UnhappyModal title={Locale.translate(Translation.SC_NO_BASKET_DETAILS)} />
  }

  if (basketAllocation.userIdWithLock !== authorisedUser.username) {
    return <UnhappyModal title={collateralBasket.name} message={Locale.translate(Translation.SC_NOT_ALLOCATION_AUTHOR)} onClose={onClose} />
  }

  const onCollateralAllocationChange = (
    originalCollateralAllocation: CollateralAllocation | undefined,
    partialUpdatedCollateralAllocation: Partial<CollateralAllocation> | undefined
  ) => {
    const collateralAllocations = allocationDetails.reduce(
      (allocations, allocationDetail) =>
        allocationDetail.collateralAllocation === originalCollateralAllocation && partialUpdatedCollateralAllocation === undefined
          ? allocations
          : [
              ...allocations,
              {
                ...allocationDetail.collateralAllocation,
                ...(allocationDetail.collateralAllocation === originalCollateralAllocation && partialUpdatedCollateralAllocation),
                bondPrice: null
              }
            ],

      []
    )

    if (originalCollateralAllocation === undefined) {
      collateralAllocations.push({
        ...(partialUpdatedCollateralAllocation as CollateralAllocation),
        bondPrice: null,
        startConsiderationOverride:
          startConsiderationOverrideEnabled && tradingType === TradingType.CASH && productSector?.startConsiderationOverrideThreshold !== 0
      })
    }

    setClipboardIssues([])
    onSaveAllocations({
      collateralAllocations,
      dealId,
      calculatingTradingType,
      haircutType: basketAllocation.haircutType,
      trimIndex: null,
      applyDefaultHaircut: false,
      source: SaveAllocationsSource.USER_ENTERED
    })
  }

  const onHaircutTypeChange = (haircutType: HaircutType | null) => {
    setClipboardIssues([])
    onSaveAllocations({
      collateralAllocations: allocationDetails.map(allocationDetail => ({
        ...allocationDetail.collateralAllocation,
        allInPrice: null,
        bondPrice: null
      })),
      dealId,
      calculatingTradingType,
      haircutType,
      trimIndex: null,
      applyDefaultHaircut: false,
      source: SaveAllocationsSource.USER_ENTERED
    })
  }

  const onSetTrimOrFill = (index: number) => {
    setClipboardIssues([])
    onSaveAllocations({
      collateralAllocations: allocationDetails.map(allocationDetail => ({
        ...allocationDetail.collateralAllocation,
        bondPrice: null
      })),
      dealId,
      calculatingTradingType,
      haircutType: basketAllocation.haircutType,
      trimIndex: index,
      applyDefaultHaircut: false,
      source: SaveAllocationsSource.USER_ENTERED
    })
  }

  const handleCancel = () => {
    onCancelAllocations(dealId)
    handleClose()
  }

  const handleAllocate = () => {
    const remainingAllocationRequired = basketAllocation?.remainingAllocationRequired || 0
    if (remainingAllocationRequired === 0) {
      onAllocate(dealId)
      if (isOpenFin()) {
        // HACK: delay on openfin the closing so we get to see possible server errors warnings
        // before the pop up window closes
        setTimeout(() => handleClose(), 2000)
      } else {
        handleClose()
      }
    } else {
      const remainingAllocationText = Locale.formatNumber(Math.abs(remainingAllocationRequired), { minDp: 2 })
      onShowDialog({
        type: NotificationType.Confirm,
        titleText:
          remainingAllocationRequired < 0
            ? Locale.translate(Translation.TC_PROMPT_TITLE_OVER_ALLOCATION)
            : Locale.translate(Translation.TC_PROMPT_TITLE_UNDER_ALLOCATION),
        messageText:
          remainingAllocationRequired < 0
            ? Locale.translate(Translation.SC_PROMPT_MSG_ACCEPT_OVER_ALLOCATION, { remainingAllocationText })
            : Locale.translate(Translation.SC_PROMPT_MSG_ACCEPT_UNDER_ALLOCATION, { remainingAllocationText }),
        buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
        defaultButton: Translation.TC_BUTTON_YES,
        cancelButton: Translation.TC_BUTTON_NO,
        responseMap: {
          [Translation.TC_BUTTON_YES]: [
            () => {
              onAllocate(dealId)
              isOpenFin() ? setTimeout(() => handleClose(), 2000) : handleClose()
            }
          ]
        }
      })
    }
  }

  const handleClose = () => {
    if (onClose) {
      onClose()
    }

    if (isOpenFin()) {
      fin.desktop.Window.getCurrent().close()
    }
  }

  const canAllocate = () => {
    const globalValidationIssues = basketAllocation?.validationIssues ?? []
    const allocationValidationIssues = flatten(
      allocationDetails.map(allocationDetail => allocationDetail.collateralAllocation.validationIssues ?? [])
    )
    return (
      [...globalValidationIssues, ...allocationValidationIssues].filter(validationIssue => validationIssue.severity === Severity.ERROR)
        .length === 0 && canAllocateCollaterals(dealDetail)
    )
  }

  const tradableAllocationDetails = allocationDetails.filter(allocationDetail => allocationDetail.collateralAllocation.canTrade) ?? []

  const handleUploadSaveAllocations = (allocations: AllocationData[]) => {
    const uploadedCollateralAllocations = allocations.reduce((collateralAllocations, allocation) => {
      const allocationDetail = allocationDetails.find(
        allocationDetail => allocationDetail.collateral.id === allocation.isin || allocationDetail.collateral.isin === allocation.isin
      )
      return allocationDetail
        ? [
            ...collateralAllocations,
            {
              ...allocationDetail.collateralAllocation,
              ...allocation,
              bondPrice: null,
              selected: true
            }
          ]
        : collateralAllocations
    }, [])

    const existingCollateralAllocations = allocationDetails.map(allocationDetail => {
      return { ...allocationDetail.collateralAllocation, bondPrice: null }
    })

    const collateralAllocations = [...existingCollateralAllocations, ...uploadedCollateralAllocations]

    const pastedTradingType = allocations.some(allocation => allocation.notional) ? TradingType.NOTIONAL : TradingType.CASH
    onSetCalculatingTradingType(pastedTradingType)

    onSaveAllocations({
      collateralAllocations: collateralAllocations,
      dealId,
      calculatingTradingType: pastedTradingType,
      haircutType: basketAllocation.haircutType,
      trimIndex: null,
      applyDefaultHaircut: false,
      source: SaveAllocationsSource.SPREADSHEET_UPLOAD
    })
  }

  const handlePaste = (clipboard: string) =>
    showModal(
      <CollateralBasketAllocationUploadDialog
        initialClipboard={clipboard}
        allocationDetails={tradableAllocationDetails}
        dealDetail={dealDetail}
        onSaveAllocations={handleUploadSaveAllocations}
      />
    )
  const handleDropFile = (files: File[]) =>
    showModal(
      <CollateralBasketAllocationUploadDialog
        initialFile={files[0]}
        allocationDetails={tradableAllocationDetails}
        dealDetail={dealDetail}
        onSaveAllocations={handleUploadSaveAllocations}
      />
    )

  const DialogContent = (
    <>
      <Content>
        <DetailsContainer>
          <Tabs dataTestId={'CollateralBasketAllocationDialog'} selectedTab={selectedTab} onChangeTab={setSelectedTab} tabs={tabs} />
          <TabbedContent>
            {selectedTab === Translation.TC_OVERVIEW && (
              <ReadOnlyCollateralOverviewPanel
                collateralBasketDetail={{
                  collateralBasket,
                  productSector
                }}
              />
            )}
            {selectedTab === Translation.TC_PRODUCTS && (
              <AvailableProductsPanel
                allocationDetails={allocationDetails.filter(
                  (value, index, originalArray) => originalArray.findIndex(v => v.collateral.id === value.collateral.id) === index
                )}
                onCollateralAllocationChange={onCollateralAllocationChange}
              />
            )}
          </TabbedContent>
        </DetailsContainer>
        <AllocationContainer>
          <AllocationsPanel
            totalAmountRequired={tradingType === TradingType.CASH ? startConsideration : notional}
            allocationDetails={allocationDetails}
            basketAllocation={basketAllocation}
            tradingType={tradingType}
            ownOrder={ownOrder}
            direction={direction}
            repoType={repoType}
            clipboardIssues={clipboardIssues}
            onCollateralAllocationChange={onCollateralAllocationChange}
            onHaircutTypeChange={onHaircutTypeChange}
            onSetTrimOrFill={onSetTrimOrFill}
            onSetCalculatingTradingType={onSetCalculatingTradingType}
          />
        </AllocationContainer>
      </Content>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton
              dataTestId={'CollateralBasketAllocationDialog-TextButton-CancelButton'}
              type={TextButtonType.Tertiary}
              onClick={handleCancel}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              dataTestId={'CollateralBasketAllocationDialog-TextButton-AllocateCollateralButton'}
              disabled={!canAllocate()}
              type={TextButtonType.Primary}
              onClick={handleAllocate}>
              {Locale.translate(Translation.TC_BUTTON_ALLOCATE_COLLATERAL)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </>
  )

  const title = (
    <AllocationHeader id={collateralBasket.id} name={collateralBasket.name} expiryTime={basketAllocation.endTime}>
      <UploadCollateralAllocationsButton
        dataTestId={'CollateralAllocation-UploadButton'}
        actions={[
          {
            label: Locale.translate(Translation.TC_ALLOCATION_BUTTON_UPLOAD),
            type: SplitButtonType.Primary,
            onClick: () => handleDropFile([]),
            enabled: true
          },
          {
            label: Locale.translate(Translation.TC_ALLOCATION_DOWNLOAD_SPREADSHEET),
            type: SplitButtonType.Primary,
            onClick: () => downloadUrl(allocationsTemplateUrl, Locale.translate(Translation.TC_ALLOCATION_DOWNLOAD_TEMPLATE_FILENAME)),
            enabled: true
          }
        ]}
        maxButtons={1}
      />
    </AllocationHeader>
  )

  return isOpenFin() ? (
    <OpenFinDropzone
      render={() => (
        <>
          <ModalTitle>{title}</ModalTitle>
          {DialogContent}
        </>
      )}
      onPaste={handlePaste}
      onDropFiles={handleDropFile}
    />
  ) : (
    <Dropzone render={() => <ModalWindow title={title}>{DialogContent}</ModalWindow>} onPaste={handlePaste} onDropFiles={handleDropFile} />
  )
}

export const CollateralBasketAllocationDialog = withModal(
  connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(CollateralBasketAllocationDialogComponent)
)
